import { useState, useEffect, useCallback } from "react";
import { FiPlus, FiCopy, FiTrash2, FiCheck, FiLink, FiClock, FiToggleLeft, FiToggleRight } from "react-icons/fi";
import { getMeetingTypes, createMeetingType, deleteMeetingType, updateMeetingType } from "../../api/meetingsApi";
import toast from "react-hot-toast";

const DURATION_OPTIONS = [15, 30, 45, 60, 90];

function MeetingTypeCard({ type, onDelete, onToggle, onCopy }) {
  const bookingUrl = `${window.location.origin}/book/${type.slug}`;

  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-800 text-base truncate">{type.title}</h3>
            <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
              type.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
            }`}>
              {type.is_active ? "Active" : "Paused"}
            </span>
          </div>
          {type.description && (
            <p className="text-xs text-gray-500 mb-2 line-clamp-2">{type.description}</p>
          )}
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span className="flex items-center gap-1"><FiClock size={13} /> {type.duration_mins} min</span>
            <span className="text-gray-300">·</span>
            <span>Rolling {type.booking_window_days || 15} days</span>
            <span className="text-gray-300">·</span>
            <span>{type.bookings_count} booking{type.bookings_count !== 1 ? "s" : ""}</span>
          </div>

          {/* Booking link */}
          <div className="mt-3 flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
            <FiLink size={13} className="text-blue-500 shrink-0" />
            <span className="text-xs text-gray-500 truncate flex-1">{bookingUrl}</span>
            <button
              onClick={() => onCopy(bookingUrl)}
              className="shrink-0 text-blue-600 hover:text-blue-800 transition"
              title="Copy link"
            >
              <FiCopy size={13} />
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-1.5 items-end shrink-0">
          <button
            onClick={() => onToggle(type)}
            className="text-gray-400 hover:text-gray-600 transition p-1.5"
            title={type.is_active ? "Pause" : "Activate"}
          >
            {type.is_active
              ? <FiToggleRight size={20} className="text-blue-500" />
              : <FiToggleLeft size={20} />}
          </button>
          <button
            onClick={() => onDelete(type.id)}
            className="text-gray-400 hover:text-red-500 transition p-1.5"
            title="Delete"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MeetingTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    slug: "",
    duration_mins: 30,
    booking_window_days: 15,
    description: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMeetingTypes();
      setTypes(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Failed to load meeting types");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-generate slug from title
  const handleTitleChange = (e) => {
    const title = e.target.value;
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    setForm(prev => ({ ...prev, title, slug }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.slug.trim()) {
      toast.error("Title and slug are required");
      return;
    }
    setSaving(true);
    try {
      await createMeetingType(form);
      toast.success("Meeting type created!");
      setShowForm(false);
      setForm({ title: "", slug: "", duration_mins: 30, booking_window_days: 15, description: "" });
      load();
    } catch (e) {
      toast.error(e.message || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this meeting type? All future bookings will be lost.")) return;
    try {
      await deleteMeetingType(id);
      toast.success("Meeting type deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleToggle = async (type) => {
    try {
      await updateMeetingType(type.id, { is_active: !type.is_active });
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied!");
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Meeting Types</h1>
          <p className="text-sm text-gray-500 mt-0.5">Create shareable booking links for different meeting formats</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition font-medium"
        >
          <FiPlus size={15} />
          New Meeting Type
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="bg-white border rounded-xl p-6 shadow-sm mb-6">
          <h2 className="font-semibold text-gray-800 mb-4">Create Meeting Type</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={handleTitleChange}
                  placeholder="e.g. 30-Min Discovery Call"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Slug (URL) *</label>
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
                  <span className="bg-gray-50 px-3 py-2.5 text-xs text-gray-400 border-r border-gray-200 shrink-0">/book/</span>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={e => setForm(prev => ({ ...prev, slug: e.target.value }))}
                    placeholder="discovery-call"
                    className="flex-1 px-3 py-2.5 text-sm focus:outline-none"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Duration</label>
                <select
                  value={form.duration_mins}
                  onChange={e => setForm(prev => ({ ...prev, duration_mins: parseInt(e.target.value) }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {DURATION_OPTIONS.map(d => (
                    <option key={d} value={d}>{d} minutes</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Booking Window (Days into future)</label>
                <input
                  type="number"
                  min="1"
                  max="180"
                  value={form.booking_window_days || 15}
                  onChange={e => setForm(prev => ({ ...prev, booking_window_days: parseInt(e.target.value) || 15 }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="15"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Description (optional)</label>
              <textarea
                value={form.description}
                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What is this meeting for?"
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-60"
              >
                {saving ? (
                  <><span className="w-4 h-4 border-2 border-white border-r-transparent rounded-full animate-spin" /> Creating...</>
                ) : (
                  <><FiCheck size={15} /> Create</>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Meeting Types List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-blue-600 border-r-transparent rounded-full animate-spin" />
        </div>
      ) : types.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
          <FiLink size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No meeting types yet</p>
          <p className="text-sm text-gray-400 mt-1">Create one to get your shareable booking link.</p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
          >
            <FiPlus size={14} /> Create First Meeting Type
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {types.map(type => (
            <MeetingTypeCard
              key={type.id}
              type={type}
              onDelete={handleDelete}
              onToggle={handleToggle}
              onCopy={handleCopy}
            />
          ))}
        </div>
      )}
    </div>
  );
}
